import { ComponentProps, useRef, useEffect } from 'react';
import {
  Chevron,
  DayButton,
  DayPicker,
  getDefaultClassNames,
} from 'react-day-picker';
import { enUS, ru } from 'react-day-picker/locale';

import cn from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './Calendar.module.scss';

import { P1, P3 } from '../Typography';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: ComponentProps<typeof DayPicker>) {
  const {
    i18n: { language },
  } = useTranslation();
  const locale = language === 'ru' ? ru : enUS;
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      locale={locale}
      showOutsideDays={showOutsideDays}
      className={cn(styles.calendar, className)}
      captionLayout={captionLayout}
      disabled={{ before: new Date() }}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn(styles.calendar__root, defaultClassNames.root),
        months: cn(styles.calendar__months, defaultClassNames.months),
        month: cn(styles.calendar__month, defaultClassNames.month),
        nav: cn(styles.calendar__nav, defaultClassNames.nav),
        button_previous: cn(
          styles.calendar__navButton,
          defaultClassNames.button_previous
        ),
        button_next: cn(
          styles.calendar__navButton,
          defaultClassNames.button_next
        ),
        month_caption: cn(
          styles.calendar__monthCaption,
          defaultClassNames.month_caption
        ),
        dropdowns: cn(styles.calendar__dropdowns, defaultClassNames.dropdowns),
        dropdown_root: cn(
          styles.calendar__dropdownRoot,
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(styles.calendar__dropdown, defaultClassNames.dropdown),
        caption_label: cn(
          styles.calendar__captionLabel,
          captionLayout === 'label'
            ? styles.calendar__captionLabelLabel
            : styles.calendar__captionLabelDropdown,
          defaultClassNames.caption_label
        ),
        table: styles.calendar__table,
        weeks: cn(styles.calendar__weeks, defaultClassNames.weeks),
        weekdays: cn(styles.calendar__weekdays, defaultClassNames.weekdays),
        weekday: cn(styles.calendar__weekday, defaultClassNames.weekday),
        week: cn(styles.calendar__week, defaultClassNames.week),
        week_number_header: cn(
          styles.calendar__weekNumberHeader,
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          styles.calendar__weekNumber,
          defaultClassNames.week_number
        ),
        day: cn(styles.calendar__day, defaultClassNames.day),
        range_start: cn(
          styles.calendar__rangeStart,
          defaultClassNames.range_start
        ),
        range_middle: cn(
          styles.calendar__rangeMiddle,
          defaultClassNames.range_middle
        ),
        range_end: cn(styles.calendar__rangeEnd, defaultClassNames.range_end),
        today: cn(styles.calendar__today, defaultClassNames.today),
        outside: cn(styles.calendar__outside, defaultClassNames.outside),
        disabled: cn(styles.calendar__disabled, defaultClassNames.disabled),
        hidden: cn(styles.calendar__hidden, defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <Chevron
                orientation="left"
                className={cn(styles.calendar__chevron, className)}
                {...props}
              />
            );
          }

          if (orientation === 'right') {
            return (
              <Chevron
                orientation="right"
                className={cn(styles.calendar__chevron, className)}
                {...props}
              />
            );
          }

          return (
            <Chevron
              orientation="down"
              className={cn(styles.calendar__chevron, className)}
              {...props}
            />
          );
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className={styles.calendar__weekNumberCell}>{children}</div>
            </td>
          );
        },
        Weekday: ({ children, ...props }) => {
          return (
            <td {...props}>
              <P3
                className={cn(
                  styles.calendar__weekday,
                  defaultClassNames.weekday
                )}
              >
                {children}
              </P3>
            </td>
          );
        },
        CaptionLabel: ({ children, ...props }) => {
          return (
            <P1 as="span" {...props}>
              {children}
            </P1>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        styles.calendar__dayButton,
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
